import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import PeriodFilter from './PeriodFilter';
import StatTile from './StatTile';
import FieldsBreakdown from './FieldsBreakdown';
import StatisticsSection from './StatisticsSection';
import { STATUS_COLORS } from './chartTheme';
import { formatPercent } from './formatters';
import LoadingBall from '../loading/LoadingBall';
import { getAllComplexGames } from '../../api/reservations';
import { DailyWeather, getDailyWeather } from '../../api/weather';
import { Game } from '../../types';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import {
  countStatuses,
  countUniquePlayers,
  getFieldsStatistics,
  getPeriod,
  getStatusTotal,
  isGameInPeriod,
  getGameStart,
  StatisticsPeriod,
} from '../../utils/statistics';

// How far back to look for a player's earlier games on a field
const PREVIOUS_GAMES_MONTHS = 12;

const NO_GAMES: Game[] = [];

const groupByField = (games: Game[]) => {
  const byField = new Map<number, Game[]>();

  games.forEach((game) => {
    if (!game.field) {
      return;
    }

    byField.set(game.field.id, [...(byField.get(game.field.id) || []), game]);
  });

  return byField;
};

interface StatisticsViewProps {
  complexId: number;
  complexName?: string;
  /** When set, statistics are limited to this field. */
  fieldId?: number;
  /** Fields to show a statistics section for. */
  fields: { id: number; name: string }[];
  openingHours?: string;
  closingHours?: string;
}

const StatisticsView = ({
  complexId,
  complexName,
  fieldId,
  fields,
  openingHours,
  closingHours,
}: StatisticsViewProps) => {
  const { t } = useTranslation();

  const [period, setPeriod] = useState<StatisticsPeriod>(() =>
    getPeriod('month', new Date())
  );
  const [games, setGames] = useState<Game[]>([]);
  const [previousGames, setPreviousGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [dailyWeather, setDailyWeather] = useState<
    Map<string, DailyWeather> | undefined
  >();

  useEffect(() => {
    let isCurrent = true;

    setDailyWeather(undefined);

    getDailyWeather(complexId, period.start, period.end)
      .then((weather) => {
        if (isCurrent) {
          setDailyWeather(weather);
        }
      })
      .catch((error) => {
        // Weather is optional; the statistics still work without it
        console.error('Error fetching weather:', error);
      });

    return () => {
      isCurrent = false;
    };
  }, [complexId, period]);

  useEffect(() => {
    let isCurrent = true;

    const fetchGames = async () => {
      setLoading(true);

      const previousStart = dayjs(period.start)
        .subtract(PREVIOUS_GAMES_MONTHS, 'month')
        .toDate();
      const previousEnd = dayjs(period.start).subtract(1, 'ms').toDate();

      const [{ data, error }, previous] = await Promise.all([
        getAllComplexGames(complexId, {
          fieldId,
          startDate: period.start,
          endDate: period.end,
        }),
        getAllComplexGames(complexId, {
          fieldId,
          startDate: previousStart,
          endDate: previousEnd,
        }),
      ]);

      if (!isCurrent) {
        return;
      }

      // Without earlier games every player simply counts as new
      setPreviousGames(
        (previous.data || []).filter((game) =>
          getGameStart(game).isBefore(dayjs(period.start))
        )
      );

      setLoading(false);

      if (error || !data) {
        toast.error(t(COMMON_TRANSLATE_KEYS.STATS_LOAD_ERROR), {
          toastId: 'statistics-load-error',
        });
        setGames([]);
        return;
      }

      setGames(
        data.filter(
          (game) =>
            isGameInPeriod(game, period) &&
            (!fieldId || game.field?.id === fieldId)
        )
      );
    };

    fetchGames();

    return () => {
      isCurrent = false;
    };
  }, [complexId, fieldId, period, t]);

  const statuses = useMemo(() => countStatuses(games), [games]);
  const players = useMemo(() => countUniquePlayers(games), [games]);
  const fieldsStatistics = useMemo(
    () => getFieldsStatistics(games, fields),
    [games, fields]
  );

  const gamesByField = useMemo(() => groupByField(games), [games]);
  const previousGamesByField = useMemo(
    () => groupByField(previousGames),
    [previousGames]
  );

  const total = getStatusTotal(statuses);

  return (
    <div className="flex flex-col gap-4 text-mainWhite">
      <PeriodFilter period={period} onChange={setPeriod} />

      {loading ? (
        <LoadingBall className="w-full h-[400px]" />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile
              label={t(COMMON_TRANSLATE_KEYS.STATS_TOTAL_RESERVATIONS)}
              value={total}
            />
            <StatTile
              label={t(COMMON_TRANSLATE_KEYS.STATS_CONFIRMED)}
              value={statuses.confirmed}
              percent={formatPercent(statuses.confirmed, total)}
              color={STATUS_COLORS.confirmed}
            />
            <StatTile
              label={t(COMMON_TRANSLATE_KEYS.STATS_DECLINED)}
              value={statuses.declined}
              percent={formatPercent(statuses.declined, total)}
              color={STATUS_COLORS.declined}
            />
            <StatTile
              label={t(COMMON_TRANSLATE_KEYS.STATS_UNANSWERED)}
              value={statuses.unanswered}
              percent={formatPercent(statuses.unanswered, total)}
              color={STATUS_COLORS.unanswered}
            />
            <StatTile
              label={t(
                fieldId
                  ? COMMON_TRANSLATE_KEYS.STATS_PLAYERS_VISITED_FIELD
                  : COMMON_TRANSLATE_KEYS.STATS_PLAYERS_VISITED_COMPLEX
              )}
              value={players}
            />
          </div>

          {!fieldId && (
            <StatisticsSection
              scope="complex"
              title={`${t(COMMON_TRANSLATE_KEYS.STATS_WHOLE_COMPLEX)}${
                complexName ? `: ${complexName}` : ''
              }`}
              games={games}
              previousGames={previousGames}
              period={period}
              openingHours={openingHours}
              closingHours={closingHours}
              dailyWeather={dailyWeather}
            >
              {fieldsStatistics.length > 0 && (
                <FieldsBreakdown fields={fieldsStatistics} />
              )}
            </StatisticsSection>
          )}

          {fields.map((field) => (
            <StatisticsSection
              key={field.id}
              scope="field"
              title={fieldId ? undefined : field.name}
              games={gamesByField.get(field.id) || NO_GAMES}
              previousGames={previousGamesByField.get(field.id) || NO_GAMES}
              period={period}
              openingHours={openingHours}
              closingHours={closingHours}
              dailyWeather={dailyWeather}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default StatisticsView;
